/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useMemo} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {loadUsers} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {GenericObject, Link} from '@ion/core/src/types/entities';
import {UserSort} from '@ion/core/src/types/services';
import {
  createKey,
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface UseInfiniteRolesQueryProps {
  accountId: number;
  searchString: any;
  includeSubGroups?: boolean;
  staleTime?: number | DurationResult;
  sortOn?: UserSort[];
  maxResults?: number;
  enabled?: boolean;
}

export interface UseInfiniteRolesQueryResult {
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  data: RoleResult[];
}

export type RoleResult = {
  name: string;
  roles: Link[];
  id: number;
  account?: GenericObject;
  accountId: number;
  searchTerm?: string;
};

interface LoadRolesResult {
  totalCount: number;
  users: RoleResult[];
};

/**
 * The `useInfiniteRolesQuery` hook provides a paginated query for retrieving
 * `Roles` for use within an infinite scroll UI.
 *
 * ```typescript
 *   import {useInfiniteRolesQuery} from '@ion/query/src/hooks/useInfiniteRolesQuery';
 *
 *   ...
 *
 *   const {
 *     isFetchingNextPage,
 *     fetchNextPage,
 *     hasNextPage,
 *     data,
 *   } = useInfiniteRolesQuery({
 *     accountId    : 212900,
 *     searchString : 'Test Role 3',
 *     enabled      : searchString.length > 2
 *   });
 *
 * ```
 *
 * @param accountId    The account ID to search for roles
 * @param searchString The search string to filter the roles
 * @param staleTime    The time in milliseconds to keep the query data in cache
 * @param sortOn       The sort order for the results
 * @param maxResults   The number of results to return per page
 * @param enabled      When true, the query will be executed immediately.
 *
 * @returns a list of role for the Users Grid Role Search Dropdown.
 *
 */
export const useInfiniteRolesQuery = ({
  searchString,
  accountId,
  sortOn = ['+username'],
  includeSubGroups = false,
  maxResults = 100,
  staleTime = getStaleTime(0, false, false),
  enabled = true
}: UseInfiniteRolesQueryProps) => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (createKey(
    QueryKey.SEARCH_ROLES,
    searchString,
    accountId
  )), [searchString, accountId]);

  const fetchRoleResults = async({pageParam = 1}): Promise<LoadRolesResult> => {
    try {
      const reponse = await loadUsers({
        accountId : Number(accountId),
        name      : searchString,
        pageSize  : maxResults,
        pageId    : pageParam,
        includeSubGroups,
        maxResults,
        sortOn
      }) as LoadRolesResult;
      return reponse;
    } catch (err) {
      error(err);
      return {totalCount: 0, users: []};
    }
  };

  const getNextPageParam = (lastPage, allPages) => {
    const allFetchedItems = allPages?.reduce((total, page) => total + page?.users?.length, 0);
    if (allFetchedItems >= lastPage?.totalCount) {
      return undefined;
    }
    return allPages?.length + 1;
  };

  const query = useInfiniteQuery<LoadRolesResult>(key, fetchRoleResults, {
    staleTime : staleTime as number,
    getNextPageParam,
    enabled
  });

  const transformedData = query.data?.pages?.flatMap(page => (
    page?.users?.map((user: RoleResult) => ({
      searchTerm : searchString,
      accountId  : entityId(user?.account),
      name       : user?.roles,
      id         : user?.id
    })
    ))) || [];

  useEffect(() => {
    queryClient.invalidateQueries([QueryKey.SEARCH_ROLES, accountId, searchString]);
  }, [accountId, searchString, includeSubGroups]);

  return createQueryResult(query, {
    isFetchingNextPage : query.isFetchingNextPage,
    fetchNextPage      : query.fetchNextPage,
    hasNextPage        : query.hasNextPage,
    isLoading          : query.isLoading,
    data               : transformedData
  } as Partial<UseDefaultQueryResult> & UseInfiniteRolesQueryResult);
};

export default useInfiniteRolesQuery;
